/**
 * Collection screen (M1c) — a gallery of the WHOLE roster: owned cards in full colour (with their
 * shard/level footer) and unowned ones LOCKED (dimmed), so it reads as a collection to complete and
 * makes shards (the dupe payout) visible. Pure DOM/UI; reuses cardFace.ts. Opened from the start screen.
 */
import type { Club, DefenseCard } from '../sim/types.ts';
import { fillCardFace } from './cardFace.ts';

export interface OwnedEntry {
  readonly count: number;
  readonly level: number;
  readonly shards: number;
}

export interface CollectionDeps {
  readonly offense: readonly Club[];
  readonly defense: readonly DefenseCard[];
  /** Owned cards keyed by id, or null when the economy is unavailable (⇒ treat all as owned). */
  owned(): ReadonlyMap<string, OwnedEntry> | null;
}

export interface Collection {
  open(): void;
  readonly el: HTMLElement;
}

const STYLE_ID = 'fc-coll-style';
const CSS = `
.fc-coll{position:absolute;inset:0;z-index:61;display:none;align-items:center;justify-content:center;
  background:rgba(6,12,18,.84);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);
  padding:calc(14px + env(safe-area-inset-top)) 12px calc(14px + env(safe-area-inset-bottom));font-family:var(--font-pixel,monospace);}
.fc-coll.is-open{display:flex;}
.fc-coll__panel{width:min(560px,96vw);max-height:94%;display:flex;flex-direction:column;background:var(--dusk-2,#1a3145);
  border:1px solid #2c4255;border-radius:16px;box-shadow:0 16px 48px rgba(0,0,0,.55);color:#f4efe2;padding:16px;}
.fc-coll__head{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;}
.fc-coll__title{font-size:12px;color:var(--gold,#f2c14e);}
.fc-coll__count{font-size:9px;opacity:.7;margin-left:auto;margin-right:10px;}
.fc-coll__x{appearance:none;border:1px solid #2c4255;background:rgba(8,16,22,.6);color:#f4efe2;width:30px;height:30px;
  border-radius:8px;font-size:15px;line-height:1;cursor:pointer;}
.fc-coll__scroll{overflow-y:auto;padding:4px 2px 8px;}
.fc-coll__sec{font-size:8px;letter-spacing:.05em;text-transform:uppercase;opacity:.6;margin:12px 0 10px;}
.fc-coll__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(74px,1fr));gap:12px 10px;}
.fc-coll__cell{position:relative;}
.fc-coll__cell .fc-card{width:100%;height:104px;}
.fc-coll__foot{position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);font-size:7px;background:#2c4255;
  color:#cfd9e2;padding:2px 7px;border-radius:999px;white-space:nowrap;}
`;

function ensureStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

export function createCollection(root: HTMLElement, deps: CollectionDeps): Collection {
  ensureStyle();

  const el = document.createElement('div');
  el.className = 'fc-coll';
  const panel = document.createElement('div');
  panel.className = 'fc-coll__panel';
  const head = document.createElement('div');
  head.className = 'fc-coll__head';
  const title = document.createElement('div');
  title.className = 'fc-coll__title';
  title.textContent = 'Collection';
  const count = document.createElement('div');
  count.className = 'fc-coll__count';
  const x = document.createElement('button');
  x.type = 'button';
  x.className = 'fc-coll__x';
  x.textContent = '✕';
  x.addEventListener('click', () => el.classList.remove('is-open'));
  head.append(title, count, x);
  const scroll = document.createElement('div');
  scroll.className = 'fc-coll__scroll';
  panel.append(head, scroll);
  el.appendChild(panel);
  root.appendChild(el);

  function section(label: string, cards: readonly (Club | DefenseCard)[], owned: ReadonlyMap<string, OwnedEntry> | null): HTMLElement {
    const sec = document.createElement('div');
    const h = document.createElement('div');
    h.className = 'fc-coll__sec';
    h.textContent = label;
    const grid = document.createElement('div');
    grid.className = 'fc-coll__grid';
    for (const card of cards) {
      const entry = owned === null ? { count: 1, level: 1, shards: 0 } : owned.get(card.id);
      const cell = document.createElement('div');
      cell.className = 'fc-coll__cell';
      const face = document.createElement('div');
      face.className = `fc-card fc-card--${'power' in card ? 'off' : 'def'}`;
      fillCardFace(face, card);
      if (!entry) face.classList.add('is-locked');
      cell.appendChild(face);
      if (entry) {
        const bits: string[] = [];
        if (entry.level > 1) bits.push(`Lv${entry.level}`);
        if (entry.shards > 0) bits.push(`${entry.shards}⚙`);
        if (bits.length > 0) {
          const foot = document.createElement('div');
          foot.className = 'fc-coll__foot';
          foot.textContent = bits.join(' ');
          cell.appendChild(foot);
        }
      }
      grid.appendChild(cell);
    }
    sec.append(h, grid);
    return sec;
  }

  function render(): void {
    const owned = deps.owned();
    const total = deps.offense.length + deps.defense.length;
    const have = owned === null ? total : owned.size;
    count.textContent = `${have} / ${total}`;
    scroll.replaceChildren(section('Clubs', deps.offense, owned), section('Defense', deps.defense, owned));
  }

  return {
    el,
    open(): void {
      render();
      el.classList.add('is-open');
    },
  };
}
